import { useState } from "react";
import { useUser } from "../../context/userContext";
import { Link } from "react-router-dom";
import SectionTitle from "../Common/sectionTitle.component";
import OfferList from "./offerList";
import PricingBox from "./pricingBox";
import { ReactComponent as CloseIcon } from "../../assets/icons/close_icon.svg";
import { ReactComponent as PaymentSuccessIcon } from "../../assets/icons/payment_success_icon.svg";

const Pricing = () => {

  const {userData} = useUser();

  const [isMonthly, setIsMonthly] = useState(true);
  const [isPaymentSuccessDialogOpen, setIsPaymentSuccessDialogOpen] =
    useState(false);

  return (
    <>
      {/* Payment Success Dialog */}
      {isPaymentSuccessDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[100]">
          <div className="fixed inset-0 bg-black/40" />

          <div className="relative flex flex-col justify-center items-center p-4 w-[90%] md:w-2/3 lg:w-3/4 xl:w-1/3 bg-white rounded-xl">
            <div className="absolute top-3 right-3 flex rounded-full cursor-pointer hover:scale-[105%]">
              <CloseIcon
                className="w-8 h-8"
                onClick={() => setIsPaymentSuccessDialogOpen(false)}
              />
            </div>
            <PaymentSuccessIcon className="mt-4 w-48 h-48" />
            <div className="flex flex-col justify-center items-center mb-8">
              <h2 className="mt-4 text-gray-900 text-2xl font-semibold">
                Payment Successful
              </h2>
              <p className="mt-2 mx-2 text-center text-sm md:text-base text-gray-400 font-medium">
                Your payment was successful. You can now continue{" "}
                <br className="hidden md:block" /> using XplorEase.
              </p>
            </div>
            <div className="flex justify-center items-center w-4/5 mb-4 bg-primary/10 rounded">
              <button className="flex justify-center items-center w-4/5 bg-primary text-white m-4 px-2 py-2 rounded">
                <Link to="/dashboard">Go to Dashboard</Link>
              </button>
            </div>
          </div>
        </div>
      )}

      <section id="pricing" className="relative z-10 py-16 md:py-20 lg:py-28">
        <div className="container">
          <SectionTitle
            title="Simple and Affordable Pricing"
            paragraph="We have the best pricing for everyone."
            center
            width="665px"
          />

          <div className="w-full">
            <div
              className="wow fadeInUp mb-8 flex justify-center md:mb-12 lg:mb-16"
              data-wow-delay=".1s"
            >
              <span
                onClick={() => setIsMonthly(true)}
                className={`${
                  isMonthly
                    ? "pointer-events-none text-primary"
                    : "text-dark dark:text-white"
                } mr-4 cursor-pointer text-base font-semibold`}
              >
                Monthly
              </span>
              <div
                onClick={() => setIsMonthly(!isMonthly)}
                className="flex cursor-pointer items-center"
              >
                <div className="relative">
                  <div className="h-5 w-14 rounded-full bg-[#1D2144] shadow-inner"></div>
                  <div
                    className={`${
                      isMonthly ? "" : "translate-x-full"
                    } shadow-switch-1 absolute left-0 top-[-4px] flex h-7 w-7 items-center justify-center rounded-full bg-primary transition`}
                  >
                    <span className="active h-4 w-4 rounded-full bg-white"></span>
                  </div>
                </div>
              </div>
              <span
                onClick={() => setIsMonthly(false)}
                className={`${
                  isMonthly
                    ? "text-dark dark:text-white"
                    : "pointer-events-none text-primary"
                } ml-4 cursor-pointer text-base font-semibold`}
              >
                Yearly
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-2 xl:bg-white xl:grid-cols-4 xl:gap-0 xl:border xl:border-neutral-200 xl xl:shadow-md">
            <PricingBox
              className="xl:border-r border-neutral-200"
              packageName="Student"
              subscription_type="student"
              subscription_frequency={isMonthly ? "monthly" : "yearly"}
              price={isMonthly ? 99 : 999}
              duration={isMonthly ? "mo" : "yr"}
              subtitle="For Students"
              setIsPaymentSuccessDialogOpen={setIsPaymentSuccessDialogOpen}
              disabled={
                userData?.subscription_type === "student" || 
                userData?.subscription_type === "pro" || 
                userData?.subscription_type === "business" || 
                userData?.subscription_type === "enterprise" ? "disabled" : ""
              }
            >
              <OfferList text="First 1 month FREE" status="active" />
              <OfferList text="1 Dynamic Virtual Assistant" status="active" />
              <OfferList
                text={`${isMonthly ? 200 : 3000} amazing queries`}
                status="active"
              />
              <OfferList text="Email Support" status="active" />
              <OfferList text="Lifetime Access" status="active" />
              <OfferList text="Free Lifetime Updates" status="active" />
            </PricingBox>
            <PricingBox
              className="xl:border-r border-neutral-200"
              packageName="Pro"
              subscription_type="pro"
              subscription_frequency={isMonthly ? "monthly" : "yearly"}
              price={isMonthly ? 299 : 1299}
              duration={isMonthly ? "mo" : "yr"}
              subtitle="For individuals and Consultants"
              setIsPaymentSuccessDialogOpen={setIsPaymentSuccessDialogOpen}
              disabled={
                userData?.subscription_type === "pro" || 
                userData?.subscription_type === "business" || 
                userData?.subscription_type === "enterprise" ? "disabled" : ""
              }
            >
              <OfferList text="First 1 month FREE" status="active" />
              <OfferList text="1 Dynamic Virtual Assistant" status="active" />
              <OfferList
                text={`${isMonthly ? 600 : 7500} amazing queries`}
                status="active"
              />
              <OfferList text="Email Support" status="active" />
              <OfferList text="Lifetime Access" status="active" />
              <OfferList text="Free Lifetime Updates" status="active" />
            </PricingBox>
            <PricingBox
              className="xl:border-r border-neutral-200"
              packageName="Business"
              subscription_type="business"
              subscription_frequency={isMonthly ? "monthly" : "yearly"}
              price={isMonthly ? 1299 : 12999}
              duration={isMonthly ? "mo" : "yr"}
              subtitle="For small & mid sized Businesses"
              setIsPaymentSuccessDialogOpen={setIsPaymentSuccessDialogOpen}
              disabled={
                userData?.subscription_type === "business" ||
                userData?.subscription_type === "enterprise" ? "disabled" : ""
              }
            >
              <OfferList text="First 1 month FREE" status="active" />
              <OfferList text="5 Dynamic Virtual Assistants" status="active" />
              <OfferList
                text={`${isMonthly ? 3000 : 26000} amazing queries`}
                status="active"
              />
              <OfferList text="Email Support" status="active" />
              <OfferList text="Lifetime Access" status="active" />
              <OfferList text="Free Lifetime Updates" status="active" />
            </PricingBox>
            <PricingBox
              packageName="Enterprise"
              subscription_type="enterprise"
              subscription_frequency={isMonthly ? "monthly" : "yearly"}
              price={isMonthly ? "Contact Sales" : "Contact Sales"}
              duration={isMonthly ? "" : ""}
              subtitle="Flexible Pay-As-You-Go Pricing"
              setIsPaymentSuccessDialogOpen={setIsPaymentSuccessDialogOpen}
              disabled={
                userData?.subscription_type === "enterprise" ? "disabled" : ""
              }
            >
              <OfferList
                text="Unlimited Dynamic Virtual Assistants" status="active"
              />
              <OfferList text="Unlimited amazing queries" status="active" />
              <OfferList text="Advanced Business Analytics" status="active" />
              <OfferList text="Email Support" status="active" />
              <OfferList text="Lifetime Access" status="active" />
              <OfferList text="Free Lifetime Updates" status="active" />
            </PricingBox>
          </div>
        </div>

        <div className="absolute left-0 bottom-0 z-[-1]">
          <svg
            width="239"
            height="601"
            viewBox="0 0 239 601"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              opacity="0.3"
              x="-184.451"
              y="600.973"
              width="196"
              height="541.607"
              rx="2"
              transform="rotate(-128.7 -184.451 600.973)"
              fill="url(#paint0_linear_93:235)"
            />
            <rect
              opacity="0.3"
              x="-188.201"
              y="385.272"
              width="59.7544"
              height="541.607"
              rx="2"
              transform="rotate(-128.7 -188.201 385.272)"
              fill="url(#paint1_linear_93:235)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_93:235"
                x1="-90.1184"
                y1="420.414"
                x2="-90.1184"
                y2="1131.65"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#3559E0" />
                <stop offset="1" stopColor="#3559E0" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_93:235"
                x1="-159.441"
                y1="204.714"
                x2="-159.441"
                y2="915.952"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#3559E0" />
                <stop offset="1" stopColor="#3559E0" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </section>
    </>
  );
};

export default Pricing;
